import { convertNotifyStaff, NOTIFY_TARGETS, WORKFLOW_LIMITS, WORKFLOW_STATUSES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Checkbox, Field, Icon, Input, Select, Switch, Textarea } from "@sagot/ui";
import { useState } from "react";
import type { AssignStep, LeadStep, NoteStep, NotifyStep, NotifyTeamStep, StatusStep, TagsStep } from "../types";
import { useRefs, type Option } from "./refs";
import { VariableList, type FormProps } from "./webhook-form";

const F = "engage.workflows.fields";

export function OptionSelect({ label, value, options, disabled, empty, onChange }: { label: string; value: string | undefined; options: Option[]; disabled: boolean; empty: string; onChange: (id: string | undefined) => void }) {
  return (
    <Field label={label}>
      <Select value={value ?? ""} disabled={disabled} onChange={(e) => { onChange(e.target.value || undefined); }}>
        <option value="">{empty}</option>
        {options.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
      </Select>
    </Field>
  );
}

/** Splits typed tags on commas and applies the server limits; `cut` says whether anything was left out. */
function parseTags(raw: string): { tags: string[]; cut: boolean } {
  const typed = raw.split(",").map((tag) => tag.trim()).filter(Boolean);
  const tags = typed.slice(0, WORKFLOW_LIMITS.tags).map((tag) => tag.slice(0, WORKFLOW_LIMITS.tag));
  const cut = typed.length > tags.length || typed.some((tag) => tag.length > WORKFLOW_LIMITS.tag);
  return { tags, cut };
}

export function TagsForm({ step, disabled, onChange }: FormProps<TagsStep>) {
  const { t } = useTranslation();
  const [text, setText] = useState(step.tags.join(", "));
  const [cut, setCut] = useState(false);
  const commit = () => {
    const parsed = parseTags(text);
    setCut(parsed.cut);
    setText(parsed.tags.join(", "));
    onChange({ ...step, tags: parsed.tags });
  };
  const hint = cut ? `${t(`${F}.tagsHint`)} ${t(`${F}.tagsCut`, { max: WORKFLOW_LIMITS.tags, chars: WORKFLOW_LIMITS.tag })}` : t(`${F}.tagsHint`);
  return (
    <Field label={t(`${F}.tags`)} hint={hint}>
      <Input value={text} disabled={disabled} onChange={(e) => { setText(e.target.value); }} onBlur={commit} />
    </Field>
  );
}

export function StatusForm({ step, disabled, onChange }: FormProps<StatusStep>) {
  const { t } = useTranslation();
  return (
    <Field label={t(`${F}.status`)}>
      <Select value={step.status} disabled={disabled} onChange={(e) => { onChange({ ...step, status: e.target.value as StatusStep["status"] }); }}>
        {WORKFLOW_STATUSES.map((status) => <option key={status} value={status}>{t(`${F}.statuses.${status}`)}</option>)}
      </Select>
    </Field>
  );
}

export function NoteForm({ step, disabled, onChange }: FormProps<NoteStep>) {
  const { t } = useTranslation();
  return (
    <div>
      <Field label={t(`${F}.note`)} hint={t(`${F}.noteHint`)}>
        <Textarea rows={4} maxLength={WORKFLOW_LIMITS.note} disabled={disabled} value={step.text} onChange={(e) => { onChange({ ...step, text: e.target.value }); }} />
      </Field>
      <VariableList />
    </div>
  );
}

export function LeadForm({ step, disabled, onChange }: FormProps<LeadStep>) {
  const { t } = useTranslation();
  const { stages, members } = useRefs();
  return (
    <div className="space-y-4">
      <OptionSelect label={t(`${F}.stage`)} value={step.stage_id} options={stages} disabled={disabled} empty={t(`${F}.unchanged`)} onChange={(stage_id) => { onChange({ ...step, stage_id }); }} />
      <OptionSelect label={t(`${F}.owner`)} value={step.owner_user_id} options={members} disabled={disabled} empty={t(`${F}.unchanged`)} onChange={(owner_user_id) => { onChange({ ...step, owner_user_id }); }} />
      <Checkbox checked={step.sync_crm} disabled={disabled} onCheckedChange={(checked) => { onChange({ ...step, sync_crm: checked === true }); }} label={t(`${F}.syncCrm`)} />
    </div>
  );
}

export function AssignForm({ step, disabled, onChange }: FormProps<AssignStep>) {
  const { t } = useTranslation();
  const { teams, members } = useRefs();
  return (
    <div className="space-y-4">
      <Field label={t(`${F}.assignTo`)}>
        <Select value={step.to} disabled={disabled} onChange={(e) => { onChange({ id: step.id, name: step.name, type: "assign", to: e.target.value as AssignStep["to"] }); }}>
          {(["team", "user", "unassign"] as const).map((to) => <option key={to} value={to}>{t(`${F}.assignTargets.${to}`)}</option>)}
        </Select>
      </Field>
      {step.to === "team" ? <OptionSelect label={t(`${F}.team`)} value={step.team_id} options={teams} disabled={disabled} empty={t(`${F}.choose`)} onChange={(team_id) => { onChange({ ...step, team_id }); }} /> : null}
      {step.to === "user" ? <OptionSelect label={t(`${F}.member`)} value={step.user_id} options={members} disabled={disabled} empty={t(`${F}.choose`)} onChange={(user_id) => { onChange({ ...step, user_id }); }} /> : null}
    </div>
  );
}

/** What "Notify staff" becomes: a "Notify team members" step with the same people and wording (`email: true` is a direct notice, `email: false` the bell only). */
const converted = (step: NotifyStep): NotifyTeamStep => convertNotifyStaff(step);

/** `onConvert` offers "Older step: convert" (not inside Apply rules, whose actions cannot be the new step). */
export function NotifyForm({ step, disabled, onChange, onConvert }: FormProps<NotifyStep> & { onConvert?: (step: NotifyTeamStep) => void }) {
  const { t } = useTranslation();
  const { teams, members } = useRefs();
  return (
    <div className="space-y-4">
      {onConvert ? (
        <div className="flex flex-col gap-2 rounded-md bg-info-soft p-3 text-xs text-fg-muted" data-testid="older-step">
          <p>{t(`${F}.olderStep`)}</p>
          <Button type="button" variant="secondary" size="sm" className="self-start" disabled={disabled} onClick={() => { onConvert(converted(step)); }}>
            <Icon name="refresh-cw" />
            {t(`${F}.convertStep`)}
          </Button>
        </div>
      ) : null}
      <Field label={t(`${F}.notifyWho`)}>
        <Select value={step.to} disabled={disabled} onChange={(e) => { onChange({ ...step, to: e.target.value as NotifyStep["to"], user_id: undefined, team_id: undefined }); }}>
          {NOTIFY_TARGETS.map((to) => <option key={to} value={to}>{t(`${F}.notifyTargets.${to}`)}</option>)}
        </Select>
      </Field>
      {step.to === "team" ? <OptionSelect label={t(`${F}.team`)} value={step.team_id} options={teams} disabled={disabled} empty={t(`${F}.choose`)} onChange={(team_id) => { onChange({ ...step, team_id }); }} /> : null}
      {step.to === "user" ? <OptionSelect label={t(`${F}.member`)} value={step.user_id} options={members} disabled={disabled} empty={t(`${F}.choose`)} onChange={(user_id) => { onChange({ ...step, user_id }); }} /> : null}
      <Field label={t(`${F}.title`)}>
        <Input maxLength={WORKFLOW_LIMITS.notifyTitle} disabled={disabled} value={step.title} onChange={(e) => { onChange({ ...step, title: e.target.value }); }} />
      </Field>
      <Field label={t(`${F}.notifyBody`)}>
        <Textarea rows={3} maxLength={WORKFLOW_LIMITS.notifyBody} disabled={disabled} value={step.body ?? ""} onChange={(e) => { onChange({ ...step, body: e.target.value || undefined }); }} />
      </Field>
      <div className="flex items-center justify-between gap-4 rounded-md bg-surface-2 px-3 py-2.5">
        <span id={`${step.id}-email`} className="text-sm font-semibold">{t(`${F}.email`)}</span>
        <Switch checked={step.email} disabled={disabled} onCheckedChange={(email) => { onChange({ ...step, email }); }} aria-labelledby={`${step.id}-email`} />
      </div>
      <VariableList />
    </div>
  );
}
